import { useEffect, useRef, useState } from "react";
import { Edit } from 'react-feather';
import { withRouter } from "react-router-dom";
import { withData } from "../../../DataController";
import { withStorage, withRemote, handleEditorURLParams, useRemote, useStorage } from "../../../Utils/Utils";
import { toastSuccess, toastWarning } from '../../popups/Toast';
import SidebarExtensionWrapper from "./SideBarExtensionWrapper";
import TemplateSelector from "./TemplateSelector";
import LoadingIcon from "../../LoadingIcon";
import ExternalManagerPortfoliosSelector from "./ExternalManagerPortfoliosSelector";
import "./ExternalManagerPortfoliosSelector.scss";
import FundSelector from "./FundSelector";
import SideBarSection from "./SideBarSection";
import Button from "../../Buttons/Button";
import Modal from "../../Modals/Modal";
import { collectEditableDataNames, resolveVideoFieldNames, isSlateContentEmpty } from "../../../Utils/customGroupsHelpers";
import { set } from "date-fns";

// Bunny Stream video status: 4 === Finished processing (mirrors VideoUploadPanel).
const BUNNY_STATUS_FINISHED = 4;
const isBlank = (v) => v == null || (typeof v === 'string' && v.trim().length === 0);


//MAP OF ALL TEMPLATE TICKER TYPES
export const TEMPLATE_TICKER_TYPES = {
    SELECTION: "selection",
    OPEN: "open",
    FIXED: "fixed",
    SCHEME: 'scheme',
    FUND: 'fund',
    BRAND: 'brand'
}

const ExternalManagerEditorSideBarExtension = (props) => {
    const { setUneditable } = props;
    const [templates, setTemplates] = useState([])
    const [selectedTemplate, setSelectedTemplate] = useState("")
    const [prevName, setPrevName] = useState("")
    const [template, setTemplate] = useState(null)
    const [products, setProducts] = useState([])
    const [portfolios, setPortfolios] = useState([])
    const [tickers, setTickers] = useState([""])
    const [selectedTickers, setSelectedTickers] = useState([""])
    const [loadingTemplate, setLoadingTemplate] = useState(false)
    const [me, setMe] = useState(null)
    const [templatesObject, setTemplatesObject] = useState({})
    const [extSideBarOptions, setExtSideBarOptions] = useState([])
    const [showRequestButton, setShowRequestButton] = useState(false)
    const [contentMode, setContentMode] = useState('editor')
    const [hasPeerReview, setHasPeerReview] = useState(null)
    const [hasComplianceReview, setHasComplianceReview] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [fundGroupAccess, setFundGroupAccess] = useState(null)
    // Pre-publish block: { header, message } when single-mode content is missing.
    const [publishBlock, setPublishBlock] = useState(null)
    const [publishChecking, setPublishChecking] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    // Re-entry locks for the review POSTs — Button `disabled` is CSS-only, so
    // a state flag alone leaves a gap before the next render.
    const publishingRef = useRef(false)
    const cancellingRef = useRef(false)

    const queryParameters = new URLSearchParams(window.location.search)
    const URLTemplate = queryParameters.get("template")
    const URLAsset = queryParameters.get("asset")
    const URLFund = queryParameters.get("fund")

    const remote = useRemote()
    const storage = useStorage()

    useEffect(() => {
        if (tickers && tickers.length > 0 && tickers[0].length > 0) {
            if (extSideBarOptions && extSideBarOptions.includes('portfolio_multiple')) props.setShowPlaceholder(false);
            props.data.updateTicker(tickers, "SIDEBAR").then(v => {
                handleEditorURLParams(template, { tickers })
                if (extSideBarOptions && !extSideBarOptions.includes('portfolio_multiple')) {
                    updateTickerStatus()
                }
            });
            // Restore the persisted editor/video mode for single-video templates.
            // Single mode has no videoModes pool (that's multiple-only), so without
            // this the toggle always defaults to Editor on load even after a save.
            if (extSideBarOptions && extSideBarOptions.includes('portfolio_single_video')) {
                const ticker = tickers[0];
                remote.get(`retrievev2/${btoa(ticker)}/${selectedTemplate}`).then(blob => {
                    const mode = (blob && blob.contentType) || 'editor';
                    setContentMode(mode);
                    const fund = portfolios.find(p => p.fundNumber === ticker);
                    props.setSelectedPortfolioMode && props.setSelectedPortfolioMode(
                        mode === 'video'
                            ? { mode: 'video', fundNumber: ticker, name: fund ? fund.fundName : ticker }
                            : { mode: 'editor', fundNumber: null, name: null }
                    );
                });
            }
        } else {
            props.setShowPlaceholder(true)
        }
    }, [tickers]);


    useEffect(() => {
        if (selectedTemplate) {
            setContentMode('editor');
            props.setSelectedPortfolioMode && props.setSelectedPortfolioMode({ mode: 'editor', fundNumber: null, name: null });
            onUpdateTemplate(URLFund && prevName === selectedTemplate ? true : null)
        }
    }, [selectedTemplate, prevName, fundGroupAccess])

    useEffect(() => {
        getExternalEditorTemplate(true)
        storage.getOrFetch('/crm/me').then(setMe)
        return () => {
            props.data.removeLocalListener(listener);
        }
    }, [])

    useEffect(() => {
        if (me && me.fundGroupAccess) {
            const fundsWithAccess = me.fundGroupAccess.flatMap(group => {
                return group.funds
            }).reduce((acc, fund) => ({ ...acc, [fund]: fund }), {})
            setFundGroupAccess(fundsWithAccess)
        }
    }, [me])

    function getExternalEditorTemplate(onMount) {
        storage.getOrFetch('/data/templates/meta').then(fetchedTemplatesMeta => {
            const templatesObj = fetchedTemplatesMeta.reduce((a, c) => ({ ...a, [c.name]: c }), {})
            setTemplatesObject(templatesObj)

            remote.get("/data/templates").then(fetchedTemplates => {
                setTemplates(fetchedTemplates)
                if (onMount && !URLTemplate && fetchedTemplates && fetchedTemplates.length === 1) {
                    setSelectedTemplate(fetchedTemplates[0])
                    onUpdateTemplate(onMount, null, templatesObj[fetchedTemplates[0]].id)
                }
            });

            if (URLTemplate && onMount) {
                const theSelectedTemplate = URLTemplate;
                setSelectedTemplate(theSelectedTemplate)
                setPrevName(theSelectedTemplate)
            }

        })
        props.data.addLocalListener(listener);
        props.data.setSaveErrorListener(saveErrorListener);
    }

    function getDocumentIdentifier() {
        let documentIdentifier = '';
        if (!template) {
            return;
        }
        const tickerConf = template.ticker;
        if (tickerConf.type === TEMPLATE_TICKER_TYPES.FUND) {
            documentIdentifier = tickers[0]
        }
        return documentIdentifier;
    }

    function onUpdateTemplate(onMount, prevTemplate, id) {
        setUneditable(false);
        if (selectedTemplate && selectedTemplate !== prevTemplate?.name) {
            setLoadingTemplate(true)

            remote.get("/data/templates/" + selectedTemplate).then(fetchedTemplate => {
                setTemplate(fetchedTemplate)
                setSelectedTickers([""])
                setTickers([""])
                setExtSideBarOptions(fetchedTemplate.ticker.extSideBarOptions)
                setLoadingTemplate(false)

                fetchedTemplate?.ticker?.type !== "fixed" ? props.setShowPlaceholder(true) : props.setShowPlaceholder(false);
                props.data.registerTemplate(fetchedTemplate, selectedTemplate);
                props.onTemplateChange(fetchedTemplate);
                handleEditorURLParams(fetchedTemplate, { selectedTemplate: selectedTemplate, tickers: tickers, noAsset: true })

                if (onMount && fetchedTemplate.ticker.type !== TEMPLATE_TICKER_TYPES.FUND) {
                    const theTickers = URLAsset ? [URLAsset] : URLFund ? [URLFund] : [""];
                    setSelectedTemplate(selectedTemplate)
                    setTickers(theTickers)
                    updateTickerStatus(true, true)
                }

                if (fetchedTemplate.ticker.type === TEMPLATE_TICKER_TYPES.FUND) {
                    const templateId = id ? id : templatesObject && templatesObject[selectedTemplate]?.id

                    Promise.all([
                        storage.getOrFetch(`/crm/templates/${templateId}/selected-tickers`),
                        storage.getOrFetch("/funds/offers?excludeBadged=true"),
                        storage.getOrFetch(`/crm/templates/${templateId}/publish-steps`)

                    ]).then(([fundsCovered, schemes, fetchedPublishSteps]) => {
                        const fundCoveredNumberSet = new Set(fundsCovered.map(f => f.ticker))
                        const portfoliosOptions = []
                        const portfoliosOptions2 = []
                        const schemeMap = new Map()
                        const schemeMap2 = new Map()

                        schemes.forEach(scheme => {
                            scheme.funds.forEach(fund => {
                                if (fundGroupAccess && fundGroupAccess[fund.id]) {
                                    portfoliosOptions2.push(fund)
                                    schemeMap2.set(fund.offerNumber, fund)
                                }
                                if (fundCoveredNumberSet.has(fund.fundNumber)) {
                                    portfoliosOptions.push(fund)
                                    schemeMap.set(fund.offerNumber, fund)
                                }
                            })
                        })
                        console.log("### Products", Array.from(schemeMap.values()))
                        console.log("### Products2", Array.from(schemeMap2.values()))
                        console.log("### portfoliosOptions", portfoliosOptions)
                        console.log("### portfoliosOptions2", portfoliosOptions2)
                        
                        setProducts(Array.from(schemeMap.values()))
                        setPortfolios(portfoliosOptions)
                        const hasPeer = peerReviewAvailable(fetchedPublishSteps)
                        const hasCompliance = complianceReviewAvailable(fetchedPublishSteps)
                        setHasPeerReview(hasPeer)
                        setHasComplianceReview(hasCompliance)

                        if (URLFund && onMount) {
                            setTickers([URLFund])
                        }
                    });
                }
            });

        } else if (template && template.ticker.type && template.ticker.type !== TEMPLATE_TICKER_TYPES.FIXED) {
            props.setShowPlaceholder(true);
            setSelectedTickers([""])
            setTickers([""])
        }
    }

    function updateTickerStatus(force, omitURL) {
        // console.log("# updateTickerStatus")
        props.setLoadingLocalData(true);

        if (template && (template.ticker.type === TEMPLATE_TICKER_TYPES.OPEN || template.ticker.type === TEMPLATE_TICKER_TYPES.FUND || template.ticker.type === TEMPLATE_TICKER_TYPES.BRAND)) {
            const noChange = scalarCompareArrays(tickers, selectedTickers);
            if (noChange && !force) {
                props.setLoadingLocalData(false);
                props.setShowPlaceholder(true);
                return;
            }
            const documentIdentifier = getDocumentIdentifier();

            if (tickers && tickers.length > 0) {
                if (tickers.length === 1 && tickers[0] === '') {
                    props.setLoadingLocalData(false);
                    props.setShowPlaceholder(true);
                    return;
                }

                if (documentIdentifier && selectedTemplate) {
                    if (hasComplianceReview) {
                        remote.get("/client/review/compliance/" + documentIdentifier + "/" + selectedTemplate)
                            .then(compliance => {
                                props.setCompliance(compliance);
                                if (!hasPeerReview) {
                                    setUneditable(compliance.requested);
                                }
                                setShowRequestButton(true)
                            });
                    }
                    if (hasPeerReview) {
                        remote.get("/client/review/peer/" + documentIdentifier + "/" + selectedTemplate)
                            .then(peerReview => {
                                props.setPeerReview(peerReview);
                                setUneditable(peerReview.requested);
                                setShowRequestButton(true)
                            });
                    }
                }
                const tickersToCheck = tickers;
                Promise.all(
                    tickersToCheck.map(ticker => {
                        //fetching adjusted template (do we need this call here?)
                        return remote.get(`/data/templates/${selectedTemplate}/page-expansion?ticker=${ticker}`).then((resp) => {
                            setTemplate(resp)
                            props.onTemplateChange(resp);
                            props.setShowPlaceholder(false);
                            // fetching ticker data
                            remote.get(`/data/data-status/${ticker}/${selectedTemplate}`)
                                .then(result => {
                                    props.setShowPlaceholder(false);
                                })
                        })
                    })
                ).then(() => {
                    setSelectedTickers(tickers)
                    props.setShowPlaceholder(true);
                    props.setLoadingLocalData(false);
                    props.data.updateTicker(tickers, "SIDEBAR").then(v => {
                    });
                });

            } else {
                //tickers array is < 1
                props.setLoadingLocalData(false);
                props.setShowPlaceholder(true);
                return;
            }
            switch (template.ticker.type) {
                case TEMPLATE_TICKER_TYPES.EXTERNAL:
                    if (!omitURL && tickers.length > 0) {
                        handleEditorURLParams(template, { tickers, selectedTickerGroup: null })
                    }
                    break;
            }

        } else {
            props.setLoadingLocalData(false);
            props.setShowPlaceholder(true);
        }
    }

    function scalarCompareArrays(arr1, arr2) {
        return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
    }

    function saveErrorListener(failed) {
        if (failed) {
            props.showSaveError();
        } else {
            props.hideSaveError();
        }
    }

    function listener() {
        //currently only optional pages need localdata
        if (template && template.optionalPages) {
            const dataName = "OPTIONAL_PAGES";
            const content = props.data.getLocalValue(dataName);
            if (content && content !== null && content !== "") {
                props.setOptionalPages(JSON.parse(content));
            }
        }

        return new Promise((resolve) => resolve(true));

    }

    function peerReviewAvailable(steps) {
        const peerApproval = steps && steps.length > 0 && steps?.find(s => s.stepId === 5 && s.stepDisplayName === 'Peer Approval')
        // const perms = me && me.perms;
        // if (steps?.length > 0 && peerApproval && peerApproval.active && perms && perms.includes("REQUEST_PEER")) return true
        if (peerApproval && peerApproval.active) return peerApproval
        else return null;
    }

    function complianceReviewAvailable(steps) {
        const complianceApproval = steps && steps.length > 0 && steps?.find(s => s.stepId === 4 && s.stepDisplayName === 'Compliance Approval')
        //const perms = me && me.perms;
        //if (steps?.length > 0 && complianceApproval && complianceApproval.active && perms && perms.includes("REQUEST_COMPLIANCE")) return true
        if (complianceApproval && complianceApproval.active) return complianceApproval
        else return null
    }

    // Pre-publish gate for single mode: block (no override) when the selected
    // portfolio's content is missing for its mode. Mirrors the multiple-mode
    // Move-to-Ready precheck, but rejects rather than warns. Fails open on
    // infra/network error so a transient issue can't block publishing.
    async function handlePublishClick() {
        if (publishingRef.current) return;
        publishingRef.current = true;
        setPublishChecking(true);
        try {
            if (extSideBarOptions && extSideBarOptions.includes('portfolio_multiple')) {
                await performReview();
                return;
            }
            const ticker = tickers && tickers[0];
            if (!ticker) { await performReview(); return; }
            const tdoc = await remote.get('/data/templates/' + selectedTemplate);
            const blob = (await remote.get(`retrievev2/${btoa(ticker)}/${selectedTemplate}`)) || {};
            if (contentMode === 'video') {
                const { idField } = resolveVideoFieldNames(tdoc);
                const videoId = blob[idField];
                if (isBlank(videoId)) {
                    setPublishBlock({ header: 'Video is missing', message: 'No video has been uploaded for this portfolio. Upload a video before publishing.' });
                    return;
                }
                const info = await remote.get(`bunny/videos/${videoId}`);
                const status = (info && info.data ? info.data : info)?.status;
                if (status !== BUNNY_STATUS_FINISHED) {
                    setPublishBlock({ header: 'Video is still processing', message: 'The uploaded video is still being processed by the video host. Please wait until processing finishes before publishing.' });
                    return;
                }
            } else {
                const names = collectEditableDataNames(tdoc);
                if (names.length > 0 && names.some(dn => isSlateContentEmpty(blob[dn]))) {
                    setPublishBlock({ header: 'Commentary is empty', message: 'This portfolio has no commentary content. Add content before publishing.' });
                    return;
                }
            }
            await performReview();
        } catch (e) {
            // Fail open (mirrors the multiple-mode precheck which proceeds on error).
            console.error('publish precheck failed; proceeding', e);
            await performReview();
        } finally {
            // Awaiting performReview above keeps the guard held through the
            // actual review POST, not just the precheck.
            publishingRef.current = false;
            setPublishChecking(false);
        }
    }

    function performReview() {
        const dto = { tickers: tickers, template: selectedTemplate };
        return remote.post("/client/review/ext/request", dto)
            .then(resp => {
                if (resp.success) {
                    const reviewObj = {
                        granted: false,
                        requested: true,
                        responded: false,
                        responderAction: null
                    }

                    if (hasPeerReview) {
                        props.setPeerReview(reviewObj);
                        setUneditable(true);
                    }
                    if (hasComplianceReview && !hasPeerReview) {
                        props.setCompliance(reviewObj)
                        setUneditable(true);
                    }

                    toastSuccess("Thank you.  Colonial First State has been notified that you have completed your review.")
                } else {
                    props.showModal("reviewFailure");
                    toastWarning("There was an issue completing your review. Please try again.");
                }
            });
    }

    function cancelReview() {
        if (cancellingRef.current) return;
        cancellingRef.current = true;
        setCancelling(true);
        const dto = { tickers: tickers, template: selectedTemplate };
        remote.post("/client/review/ext/request/cancel", dto).then(resp => {
            console.log("## Cancel review response:", resp)
            const reviewObj = {
                granted: false,
                requested: false,
                responded: false,
                responderAction: null
            }
            if (hasPeerReview) {
                props.setPeerReview(reviewObj);
                setUneditable(false);
            }
            if (hasComplianceReview && !hasPeerReview) {
                props.setCompliance(reviewObj)
                setUneditable(false);
            }
            toastSuccess("Your review request has been cancelled.")

        }).catch(error => {
            console.error("Error cancelling review:", error);
            toastWarning("There was an issue cancelling your review. Please try again.");
        }).finally(() => {
            cancellingRef.current = false;
            setCancelling(false);
        });

    }

    function downloadProps() {
        const props = {
            tickers: tickers,
            template: selectedTemplate
        }
        return props;
    }

    function preview() {
        setPreviewLoading(true);
        props.downloadPDF(
            downloadProps(), null, () => {
                setPreviewLoading(false);
            }
        );
    }

    const isSelection = template && template.ticker.type === TEMPLATE_TICKER_TYPES.SELECTION;
    const review = props.peerReview && props.peerReview.granted && props.compliance ? props.compliance : props.peerReview ? props.peerReview : props.compliance
    // Document is locked for review (same condition that shows "Under Review" /
    // "Cancel Review" and calls setUneditable(true)); the editor/video switch
    // must be disabled until the review is cancelled/cleared.
    const underReview = (props.peerReview && props.peerReview.requested) || (props.compliance && props.compliance.requested)

    return (
        <SidebarExtensionWrapper header={{ header: "Editor", icon: <Edit color="#0E8EFD" />, border: "bottom" }} noMargin collapsable collapsed={false} shadow sideBarLeft leftSideBarCollapsed={props?.leftSideBarCollapsed} setLeftSideBarCollapsed={props?.setLeftSideBarCollapsed}>
            <div id="editor-sidebar-body">
                {/* TEMPLATE */}
                <TemplateSelector
                    templates={templates}
                    selectedTemplate={selectedTemplate}
                    setState={(obj) => setSelectedTemplate(obj.selectedTemplate)}
                    onUpdateTemplate={onUpdateTemplate}
                    isSelection={isSelection}
                    className="pt-xl"
                />
                {/* TICKERS */}
                {!template && extSideBarOptions.length === 0 ? "" : loadingTemplate
                    ? <LoadingIcon centered size={48} style={{ paddingBottom: "2rem" }} />
                    : <>
                        {extSideBarOptions && extSideBarOptions.includes('portfolio_multiple') &&
                            <><div className="sidebar-section-divider" /><ExternalManagerPortfoliosSelector
                                portfolios={portfolios}
                                products={products}
                                onUpdateTickerGroup={null}
                                needsTickerData={template?.ticker?.needsTickerData}
                                setState={(val) => { setTickers([val]) }}
                                state={{ tickers: tickers }}
                                updateTickerStatus={null}
                                template={selectedTemplate}
                                setPortfoliosState={props.setPortfoliosState}
                                setPortfoliosGroupedState={props.setPortfoliosGroupedState}
                                showPreview={extSideBarOptions.includes('preview')}
                                showVideoToggle={extSideBarOptions.includes('portfolio_multiple_video')}
                                setPortfolioMode={props.setSelectedPortfolioMode}
                                exposeMoveToReady={props.exposeMoveToReady}
                                exposeSetMode={props.exposeSetMode}
                            /><div className="sidebar-section-divider" /></>}
                        {extSideBarOptions && extSideBarOptions.includes('portfolio_single') &&
                            <FundSelector
                                setState={(val) => { setTickers(val.tickers) }}
                                state={{ tickers: tickers }}
                                updateTickerStatus={null}
                                tickerOption={template.ticker}
                                onUpdateTickerGroup={null}
                                needsTickerData={null}
                                passedFunds={portfolios}
                            />
                        }
                        {extSideBarOptions && extSideBarOptions.includes('portfolio_single_video') && tickers && tickers[0] && tickers[0].length > 0 &&
                            <div className="ext-mode-toggle" style={underReview ? { opacity: 0.4, pointerEvents: 'none' } : null}>
                                <button
                                    className={`ext-mode-btn${contentMode === 'editor' ? ' active' : ''}`}
                                    disabled={underReview}
                                    onClick={() => {
                                        setContentMode('editor');
                                        remote.put(`savev2/${btoa(tickers[0])}/${selectedTemplate}`, { contentType: 'editor' });
                                        props.setSelectedPortfolioMode && props.setSelectedPortfolioMode({ mode: 'editor', fundNumber: null, name: null });
                                    }}
                                >Editor</button>
                                <button
                                    className={`ext-mode-btn${contentMode === 'video' ? ' active' : ''}`}
                                    disabled={underReview}
                                    onClick={() => {
                                        const fund = portfolios.find(p => p.fundNumber === tickers[0]);
                                        setContentMode('video');
                                        remote.put(`savev2/${btoa(tickers[0])}/${selectedTemplate}`, { contentType: 'video' });
                                        props.setSelectedPortfolioMode && props.setSelectedPortfolioMode({ mode: 'video', fundNumber: tickers[0], name: fund ? fund.fundName : tickers[0] });
                                    }}
                                >Video</button>
                            </div>
                        }
                        {extSideBarOptions && (extSideBarOptions.includes('preview') || extSideBarOptions.includes('submit_for_review')) && tickers && tickers.length > 0 && tickers[0].length > 0 &&
                            <SideBarSection separator className="mt-xl">
                                {extSideBarOptions.includes('preview') && contentMode !== 'video' &&
                                    <Button fullWidth onClick={preview} className={`mb-m`} disabled={previewLoading} variant="secondary">{previewLoading ? <><div className="sp-circle" /> Generating</> : "Preview"}</Button>
                                }
                                {extSideBarOptions.includes('submit_for_review') && showRequestButton && review && (props.peerReview && props.peerReview.requested) || (props.compliance && props.compliance.requested) &&
                                    <Button
                                        variant={"warning"}
                                        onClick={cancelReview}
                                        disabled={cancelling}
                                        loading={cancelling}
                                        style={{ marginBottom: "1rem" }}
                                        fullWidth
                                    >
                                        Cancel Review
                                    </Button>
                                }
                                {extSideBarOptions.includes('submit_for_review') && showRequestButton && review &&
                                    <Button
                                        noHover={review.granted || review.requested || review.responded}
                                        variant={"positive"}
                                        disabled={publishChecking || (props.peerReview && props.peerReview.requested) || (props.compliance && props.compliance.requested)}
                                        onClick={handlePublishClick}
                                        fullWidth
                                    >
                                        {(props.peerReview && props.peerReview.requested) || (props.compliance && props.compliance.requested) ? "Under Review" : publishChecking ? <><div className="sp-circle" /> Checking</> : "Publish"}
                                    </Button>
                                }
                            </SideBarSection>
                        }
                    </>
                }
            </div>
            {publishBlock && (
                <Modal
                    header={publishBlock.header}
                    handleClose={() => setPublishBlock(null)}
                    confirmButton={{ label: 'OK', variant: 'primary', onClick: () => setPublishBlock(null) }}
                >
                    <p>{publishBlock.message}</p>
                </Modal>
            )}
        </SidebarExtensionWrapper>
    );
}

export default withRouter(withRemote(withStorage(withData(ExternalManagerEditorSideBarExtension))));